import { logDebug, logWarn, logError } from './loggerService';
import { getJWT, getActiveProfileId } from './apiService';

const WS_BASE_URL = import.meta.env.VITE_WS_BASE_URL || '';
const RECONNECT_DELAY_MS = 5000;

// WebSocket state
let webSocketForActivities: WebSocket | null = null;
let isConnected = false;
let hasError = false;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

// Activity count state
let activityCount = 0;
let nackInvitationsCount = 0;

// Subscribers for state changes
type Listener<T> = (value: T) => void;
const connectionListeners: Set<Listener<boolean>> = new Set();
const activityCountListeners: Set<Listener<number>> = new Set();
const totalCountListeners: Set<Listener<number>> = new Set();

// Notify listeners
function notifyConnectionListeners(connected: boolean) {
  connectionListeners.forEach(listener => listener(connected));
}

function notifyActivityCountListeners(count: number) {
  activityCountListeners.forEach(listener => listener(count));
}

function notifyTotalCountListeners() {
  const total = activityCount + nackInvitationsCount;
  totalCountListeners.forEach(listener => listener(total));
}

// Connect to activities WebSocket
export function connectActivitiesWebSocket() {
  const func = 'connectActivitiesWebSocket';
  const jwt = getJWT();
  const profileId = getActiveProfileId();

  if (!jwt || !profileId) {
    logWarn(func, 'Cannot connect WebSocket: missing JWT or profileId');
    return;
  }

  const url = `${WS_BASE_URL}/ws/activity/count/${profileId}`;

  // Close existing connection if any
  if (webSocketForActivities) {
    webSocketForActivities.close();
    webSocketForActivities = null;
  }

  webSocketForActivities = new WebSocket(url, [jwt]);

  webSocketForActivities.onopen = () => {
    logDebug(func, 'WebSocket connection open');
    hasError = false;
    isConnected = true;
    notifyConnectionListeners(true);
  };

  webSocketForActivities.onclose = () => {
    logWarn(func, 'WebSocket connection closed');
    isConnected = false;
    notifyConnectionListeners(false);
    reconnectIfNeeded();
  };

  webSocketForActivities.onmessage = (event: MessageEvent) => {
    logDebug(func, 'WebSocket message received', { data: event.data });
    const count = parseInt(event.data.split('\n')[0], 10);
    if (!isNaN(count)) {
      activityCount = count;
      notifyActivityCountListeners(activityCount);
      notifyTotalCountListeners();
    }
  };

  webSocketForActivities.onerror = () => {
    logError(new Error('WebSocket connection error'), func);
    hasError = true;
    if (webSocketForActivities) {
      webSocketForActivities.close();
      webSocketForActivities = null;
    }
    isConnected = false;
    notifyConnectionListeners(false);
  };
}

// Disconnect WebSocket
export function disconnectActivitiesWebSocket() {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  if (webSocketForActivities) {
    webSocketForActivities.close();
    webSocketForActivities = null;
  }
  isConnected = false;
  hasError = false;
  notifyConnectionListeners(false);
}

// Reconnect logic
function reconnectIfNeeded() {
  const jwt = getJWT();
  const profileId = getActiveProfileId();

  // Only reconnect if we have auth and aren't already reconnecting
  if (jwt && profileId && !reconnectTimeout && !hasError) {
    reconnectTimeout = setTimeout(() => {
      reconnectTimeout = null;
      connectActivitiesWebSocket();
    }, RECONNECT_DELAY_MS);
  }
}

// Set invitation count (called from invitation service)
export function setNackInvitationsCount(count: number) {
  nackInvitationsCount = count;
  notifyTotalCountListeners();
}

// Subscribe to connection state changes
export function subscribeToConnection(listener: Listener<boolean>): () => void {
  connectionListeners.add(listener);
  // Immediately notify with current state
  listener(isConnected);
  return () => connectionListeners.delete(listener);
}

// Subscribe to activity count changes
export function subscribeToActivityCount(listener: Listener<number>): () => void {
  activityCountListeners.add(listener);
  listener(activityCount);
  return () => activityCountListeners.delete(listener);
}

// Subscribe to total count changes (activities + invitations)
export function subscribeToTotalCount(listener: Listener<number>): () => void {
  totalCountListeners.add(listener);
  listener(activityCount + nackInvitationsCount);
  return () => totalCountListeners.delete(listener);
}

// Getters
export function getActivityCount(): number {
  return activityCount;
}

export function getTotalCount(): number {
  return activityCount + nackInvitationsCount;
}

export function isWebSocketConnected(): boolean {
  return isConnected;
}

// Convenience export
export const webSocketService = {
  connect: connectActivitiesWebSocket,
  disconnect: disconnectActivitiesWebSocket,
  setNackInvitationsCount,
  subscribeToConnection,
  subscribeToActivityCount,
  subscribeToTotalCount,
  getActivityCount,
  getTotalCount,
  isConnected: isWebSocketConnected,
};
